import React, { useCallback, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { ClientRequest, useClient } from '../../hooks/useClient';
import { Routine, ExerciseDay } from '../../types/Fisiofit/routine';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { PrimeIcons } from 'primereact/api';
import { ItemDisplay } from '../../types/responses';
import { usePopups } from '../../context/PopupContext';
import { ToastMessage } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { RadioButton } from 'primereact/radiobutton';
import { Dropdown } from 'primereact/dropdown';
import { Capacitor } from '@capacitor/core';
import './CopyRoutineButton.scss';
import { FormField } from '../FormField/FormField';
import classNames from 'classnames';
import { AdaptiveSelect } from '../AdaptiveSelect/AdaptiveSelect';

interface RoutineListItem extends ItemDisplay {
  externalUserId?: string;
}

interface RoutineDropdownOption {
  value: string;
  label: string;
  externalUserId?: string;
}

type CopyType = 'full' | 'specific';

interface DayOption {
  value: number;
  label: string;
}

const fetchRoutine = async (
  routineDisplay: ItemDisplay,
  get: ClientRequest,
  showToast: ((message: ToastMessage | ToastMessage[]) => void) | undefined,
) => {
  const routine = await get<Routine>(`/routines/${routineDisplay.id}`);

  if (!routine) {
    showToast?.({
      severity: 'error',
      summary: 'Error copiando ejercicios',
      detail: `No se pudieron copiar los ejercicios de la rutina "${routineDisplay.name}"`,
    });
  }

  return routine;
};

const getMenuOptions = (routines: RoutineListItem[] | undefined): RoutineDropdownOption[] => {
  if (!routines) return [];
  return routines.map((routine) => ({
    value: routine.id,
    label: routine.name,
    externalUserId: routine.externalUserId ?? undefined,
  }));
};

const getDayOptions = (
  routine: Routine | null,
  totalDays: number,
  isTargetDays: boolean,
  currentDays?: ExerciseDay[],
): DayOption[] => {
  if (isTargetDays) {
    // Para el destino, incluimos los días existentes más uno adicional
    // Siempre debe haber al menos 1 día disponible (el nuevo día 1)
    const maxDays = Math.max(totalDays, 0);
    const options: DayOption[] = [];
    for (let i = 1; i <= maxDays + 1; i++) {
      // Buscar si el día ya existe en la rutina actual y tiene nombre
      const existingDay = currentDays?.find((day) => day.dayNumber === i);

      options.push({
        value: i,
        label: i <= maxDays ? existingDay?.name || `Día ${i}` : `Día ${i} (Nuevo)`,
      });
    }
    return options;
  }

  // Para el origen, necesitamos una rutina válida
  if (!routine) return [];

  // Solo incluimos los días que tienen ejercicios
  return routine.exerciseDays.map((day) => ({
    value: day.dayNumber,
    label: day.name || `Día ${day.dayNumber}`,
  }));
};

interface Props {
  onSelectRoutine: (routine: Routine, options?: { specificDay?: { sourceDay: ExerciseDay; targetDayNumber: number } }) => void;
  currentRoutine: Routine | undefined;
  currentDaysLength: number;
  loading: boolean;
}

export const CopyRoutineButton = ({ onSelectRoutine, currentRoutine, currentDaysLength, loading }: Props) => {
  const { showToast } = usePopups();
  const { organization } = useOrganizationContext();
  const { get } = useClient();
  const [isCopying, setIsCopying] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [selectedRoutineId, setSelectedRoutineId] = useState<string | null>(null);
  const [selectedRoutine, setSelectedRoutine] = useState<Routine | null>(null);
  const [copyType, setCopyType] = useState<CopyType>('full');
  const [sourceDayNumber, setSourceDayNumber] = useState<number | null>(null);
  const [targetDayNumber, setTargetDayNumber] = useState<number | null>(null);
  // The id picked last, so a late answer for an earlier pick does not replace the routine picked now.
  const pickedRoutineIdRef = useRef<string | null>(null);

  const isTouchDevice = Capacitor.isNativePlatform() || window.matchMedia('(hover: none)').matches;

  const { data: routines, isLoading } = useSWR(
    organization?.id ? `/organizations/${organization.id}/routines?short=true` : null,
    get<RoutineListItem[]>,
  );

  // The full source routine arrives after the pick (GET /routines/:id); until it matches the picked id, there is nothing to copy.
  const isSourceLoaded = selectedRoutineId !== null && selectedRoutine?.id === selectedRoutineId;

  const routineOptions = useMemo(() => getMenuOptions(routines), [routines]);
  const sourceDayOptions = useMemo(() => getDayOptions(selectedRoutine, 0, false), [selectedRoutine]);
  const targetDayOptions = useMemo(() => {
    return getDayOptions(null, currentDaysLength, true, currentRoutine?.exerciseDays);
  }, [currentDaysLength, currentRoutine?.exerciseDays]);

  const handleRoutineChange = async (newRoutineId: string) => {
    setSelectedRoutineId(newRoutineId);
    pickedRoutineIdRef.current = newRoutineId;
    setSourceDayNumber(null);
    setTargetDayNumber(null);

    if (newRoutineId) {
      try {
        const routine = routines?.find((r) => r.id === newRoutineId);
        if (routine) {
          const fullRoutine = await fetchRoutine(routine, get, showToast);
          if (fullRoutine && pickedRoutineIdRef.current === newRoutineId) {
            setSelectedRoutine(fullRoutine);
          }
        }
      } catch (error) {
        console.error('Error loading routine details:', error);
      }
    } else {
      setSelectedRoutine(null);
    }
  };

  const resetForm = () => {
    setIsDialogVisible(false);
    setSelectedRoutineId(null);
    pickedRoutineIdRef.current = null;
    setSelectedRoutine(null);
    setCopyType('full');
    setSourceDayNumber(null);
    setTargetDayNumber(null);
  };

  const selectRoutine = useCallback(async () => {
    if (!isSourceLoaded || !selectedRoutine) return;

    setIsCopying(true);

    try {
      if (copyType === 'full') {
        // Copiar rutina completa (comportamiento actual)
        const routineToPass = {
          ...selectedRoutine,
          exerciseDays: selectedRoutine.exerciseDays,
        };

        onSelectRoutine(routineToPass);
        showToast?.({
          severity: 'info',
          summary: 'Ejercicios copiados',
          detail: `Se han copiado los ejercicios de la rutina "${selectedRoutine.name}"`,
        });
      } else if (copyType === 'specific' && sourceDayNumber !== null && targetDayNumber !== null) {
        // Copiar un día específico
        const sourceDay = selectedRoutine.exerciseDays.find((day) => day.dayNumber === sourceDayNumber);

        if (sourceDay) {
          // Crear una copia de la rutina actual
          const routineCopy: Routine = { ...currentRoutine } as Routine;
          if (!routineCopy.exerciseDays) {
            routineCopy.exerciseDays = [];
          }

          // Crear o actualizar el día de destino
          const targetDayIndex = routineCopy.exerciseDays.findIndex((day) => day.dayNumber === targetDayNumber);
          const newDay: ExerciseDay = {
            dayNumber: targetDayNumber,
            exercises: [...sourceDay.exercises],
            id: targetDayIndex >= 0 ? routineCopy.exerciseDays[targetDayIndex].id : undefined,
            name: sourceDay.name,
          };

          if (targetDayIndex >= 0) {
            // Reemplazar día existente
            routineCopy.exerciseDays[targetDayIndex] = newDay;
          } else {
            // Añadir nuevo día
            routineCopy.exerciseDays.push(newDay);
          }

          onSelectRoutine(routineCopy, {
            specificDay: {
              sourceDay,
              targetDayNumber,
            },
          });

          // Encontrar los nombres de los días para el mensaje
          const sourceDayName = sourceDay.name || `Día ${sourceDayNumber}`;
          const targetDay = currentRoutine?.exerciseDays.find((day) => day.dayNumber === targetDayNumber);
          const targetDayName = targetDay?.name || `Día ${targetDayNumber}`;

          showToast?.({
            severity: 'info',
            summary: 'Día copiado',
            detail: `Se ha copiado ${sourceDayName} de la rutina "${selectedRoutine.name}" a ${targetDayName}`,
          });
        }
      }
    } catch (error) {
      console.error('Error al copiar rutina:', error);
      showToast?.({
        severity: 'error',
        summary: 'Error al copiar',
        detail: 'No se pudieron copiar los ejercicios correctamente',
      });
    } finally {
      setIsCopying(false);
      resetForm();
    }
  }, [
    isSourceLoaded,
    selectedRoutine,
    copyType,
    sourceDayNumber,
    targetDayNumber,
    onSelectRoutine,
    showToast,
    currentRoutine,
  ]);

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDialogVisible(true);
  };

  const dialogFooter = (
    <div className='dialog-footer'>
      <Button label='Cancelar' icon='pi pi-times' onClick={resetForm} className='p-button-text' />
      <Button
        label='Copiar'
        icon='pi pi-copy'
        onClick={selectRoutine}
        disabled={!isSourceLoaded || (copyType === 'specific' && (sourceDayNumber === null || targetDayNumber === null))}
        loading={isCopying}
      />
    </div>
  );

  const routineOptionTemplate = (option: RoutineDropdownOption | null) => {
    if (!option) return null;

    return (
      <div className='routine-option-item'>
        <span className={`routine-type-dot ${option.externalUserId ? 'user' : 'template'}`} />
        <span>{option.label}</span>
      </div>
    );
  };

  const copyDayOptionsDisabled = !selectedRoutineId || isCopying || copyType === 'full';
  const copyTypeOptionsDisabled = !selectedRoutineId || isCopying;

  return (
    <div className='copy-routine-button'>
      <Button
        label='Copiar ejercicios'
        icon={PrimeIcons.COPY}
        text
        onClick={handleButtonClick}
        disabled={isLoading || isCopying || loading}
        loading={isLoading || isCopying || loading}
        className='copy-button'
      />

      <Dialog
        visible={isDialogVisible}
        onHide={resetForm}
        header='Copiar ejercicios de rutina'
        footer={dialogFooter}
        className='copy-routine-dialog big-modal'
        draggable={false}
        resizable={false}
        style={{ width: isTouchDevice ? '90vw' : '550px' }}
      >
        <div className='copy-routine-dialog-content'>
          <FormField labelTitle='Rutina de origen' elementId='routine-select' className='full-width'>
            <AdaptiveSelect
              inputId='routine-select'
              value={selectedRoutineId}
              options={routineOptions}
              onChange={handleRoutineChange}
              placeholder='Selecciona una rutina'
              disabled={isLoading || isCopying}
              itemTemplate={routineOptionTemplate}
              /* valueTemplate={
                selectedRoutineId
                  ? routineOptionTemplate(routineOptions.find((o) => o.value === selectedRoutineId) || null)
                  : undefined
              } */
              filter
              filterPlaceholder='Buscar rutina...'
            />
          </FormField>

          <FormField
            className={classNames('copy-type-section', 'full-width', { 'copy-type-section-disabled': copyTypeOptionsDisabled })}
            labelTitle='Tipo de copia'
            elementId='copy-type-select'
          >
            <div className='copy-type-options'>
              <div className='copy-type-option'>
                <RadioButton
                  inputId='copy-full'
                  name='copyType'
                  value='full'
                  onChange={() => setCopyType('full')}
                  checked={copyType === 'full'}
                  disabled={copyTypeOptionsDisabled}
                />
                <label htmlFor='copy-full'>Copiar rutina completa</label>
              </div>
              <div className='copy-type-option'>
                <RadioButton
                  inputId='copy-specific'
                  name='copyType'
                  value='specific'
                  onChange={() => setCopyType('specific')}
                  checked={copyType === 'specific'}
                  disabled={copyTypeOptionsDisabled}
                />
                <label htmlFor='copy-specific'>Copiar un día específico</label>
              </div>
            </div>
          </FormField>

          <div className={classNames('copy-day-section', { 'copy-day-section-disabled': copyDayOptionsDisabled })}>
            <FormField labelTitle='Día a copiar' elementId='source-day-select'>
              <Dropdown
                id='source-day-select'
                value={sourceDayNumber}
                options={sourceDayOptions}
                onChange={(e) => setSourceDayNumber(e.value)}
                placeholder='Día de origen'
                disabled={copyDayOptionsDisabled}
              />
            </FormField>

            <FormField labelTitle='Día al que copiar' elementId='target-day-select'>
              <Dropdown
                id='target-day-select'
                value={targetDayNumber}
                options={targetDayOptions}
                onChange={(e) => setTargetDayNumber(e.value)}
                placeholder='Día de destino'
                disabled={copyDayOptionsDisabled}
              />
            </FormField>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
